import { getDisplayString } from '@medplum/core';
import { Button, Center, Loader, Stack, Text } from '@mantine/core';
import { useMedplumProfile } from '@medplum/react-hooks';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { isZoomEnabled } from '@/lib/zoom';
import { actorRefIsAppointmentParticipant } from '@/features/calendar/calendarEventGuards';
import { useCurrentUserPractitioner } from '@/hooks/useCurrentUserPractitioner';
import { useTelehealthZoomMeeting } from './hooks/useTelehealthZoomMeeting';

/**
 * Resolves Zoom meeting params from the Appointment, then opens ZoomCallPage in a new tab
 * via a user-initiated click (avoids popup blockers).
 */
export function TelehealthSessionPage(): JSX.Element {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const profile = useMedplumProfile();
  const { practitioner, loading: practitionerLoading } = useCurrentUserPractitioner();
  const zoomEnabled = isZoomEnabled();
  const { appointment, zoom, loading, waitState, checkAgain } = useTelehealthZoomMeeting(appointmentId);

  const providerName = (profile ? getDisplayString(profile) : '') || 'Provider';
  const providerEmail = profile?.telecom?.find((t) => t.system === 'email')?.value;

  const urlParams = new URLSearchParams(window.location.search);
  const queryMn = urlParams.get('zoomMeetingNumber');
  const queryPw = urlParams.get('zoomPassword') ?? '';

  const meetingNumber = zoom.meetingNumber || queryMn || '';
  const password = zoom.password || queryPw;
  const isParticipant = actorRefIsAppointmentParticipant(
    appointment?.participant,
    practitioner?.id ? `Practitioner/${practitioner.id}` : undefined,
  );

  const handleJoin = useCallback(() => {
    if (!isParticipant) return;
    const params = new URLSearchParams({
      ...(appointmentId ? { appointmentId } : {}),
      zoomMeetingNumber: meetingNumber,
      zoomPassword: password,
      zoomUserName: providerName,
      zoomUserEmail: providerEmail ?? '',
      zoomRole: '1',
    });
    window.open(`/dashboard/session/zoom-call?${params.toString()}`, '_blank', 'noopener,noreferrer');
    window.history.back();
  }, [appointmentId, meetingNumber, password, providerName, providerEmail, isParticipant]);

  if (!zoomEnabled) {
    return (
      <Center h="100vh">
        <Text c="dimmed">Zoom is not enabled.</Text>
      </Center>
    );
  }

  if (loading || practitionerLoading || waitState === 'waiting') {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed" ta="center" maw={360}>
            {waitState === 'waiting'
              ? 'Setting up your video call. This usually takes a few seconds after scheduling.'
              : 'Loading appointment…'}
          </Text>
        </Stack>
      </Center>
    );
  }

  if (!isParticipant) {
    return (
      <Center h="100vh">
        <Text c="dimmed" ta="center" maw={400}>
          You are not a participant on this meeting, so the video session is not available.
        </Text>
      </Center>
    );
  }

  if (waitState === 'timed_out') {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md" maw={400}>
          <Text c="dimmed" ta="center">
            The video call link is still being prepared. You can wait a moment and try again.
          </Text>
          <Button variant="light" onClick={() => void checkAgain()}>
            Check again
          </Button>
        </Stack>
      </Center>
    );
  }

  if (!meetingNumber) {
    return (
      <Center h="100vh">
        <Text c="dimmed">
          {waitState === 'not_expected'
            ? 'This appointment is not set up for telehealth video.'
            : 'No Zoom meeting found for this appointment.'}
        </Text>
      </Center>
    );
  }

  return (
    <Center h="100vh">
      <Stack align="center" gap="md">
        <Text size="lg" fw={500}>Ready to join telehealth session</Text>
        <Button size="lg" onClick={handleJoin}>
          Join Video Call
        </Button>
      </Stack>
    </Center>
  );
}
